import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Api } from '@core/api/Api';
import { ModalService } from '@fino-ui/modal';
import { TranslateService } from '@fino-ui/translation';
import { filter, map as lodashMap, sortBy } from 'lodash';
import { map, type Observable, Subscription } from 'rxjs';
import { ApproveModalComponent } from '../../components/approve-modal/approve-modal.component';
import { DOCUMENT_TYPES_BUTTONS } from './identify-document.constants';

export interface PadContact {
  blz: string;
  bankName: string;
  kwhEmail: string;
  finoTenant: boolean;
  countryCode: string;
  contact: {
    name: string;
    department: null;
    fax: string;
    address: string;
    postcode: string;
    city: string;
    preferredContact: null;
    country: null;
    email: null;
    useOekopost: boolean;
    ignoreCreditor: boolean;
    zapiId: null;
    countryName: null;
  };
}

@Injectable({
  providedIn: 'root',
})
export class IdentifyDocumentService {
  private readonly http = inject(HttpClient);
  private readonly translateService = inject(TranslateService);
  private modalService = inject(ModalService);

  fetchNextJob(id: string) {
    return this.http.get(
      id ? `${Api.IdentifyDocument.Id}/${id}` : `${Api.IdentifyDocument.Next}`,
    );
  }

  public deleteSubscription = Subscription.EMPTY;

  getNumJobs() {
    return this.http.get(Api.IdentifyDocument.Count).pipe(
      map((res: any) => {
        return {
          locked: {
            TOTAL: res.locked,
          },
          open: {
            TOTAL: res.open,
          },
        };
      }),
    );
  }

  submitJob(id: string, job: any) {
    if (job.newIban) {
      job.newIban = job.newIban.replace(/ /g, '');
    }
    if (job.oldIban) {
      job.oldIban = job.oldIban.replace(/ /g, '');
    }

    return this.http.put(`${Api.IdentifyDocument.Id}/${id}`, job);
  }

  deleteJob(id: string) {
    return this.http.delete(`${Api.IdentifyDocument.Id}/${id}`);
  }

  getSiblings(id: string) {
    return this.http.get(`${Api.IdentifyDocument.Id}/${id}/siblings`);
  }

  search(
    type: string,
    tenant: string,
    countryCode: string = 'DE',
    search: string = '',
  ) {
    // append trailing 'DE' if not present
    search = search.toUpperCase();
    search = search.replace(/ /g, '');
    // only append DE if it is not already present and if it is not an AT IBAN, sometimes they are to lazy to enter the country code
    if (!search.match(/^[A-Z]{2}/)) {
      search = `${countryCode}${search}`;
    }
    return this.http
      .get(Api.IdentifyDocument.Search, {
        params: {
          type,
          tenant,
          limit: 10,
          q: search,
        },
      })
      .pipe(
        map((res: any) => {
          const sorted = sortBy(res, (r) => {
            return [r.closed];
          });
          return sorted;
        }),
      );
  }

  queryBLZ(tenant: string): Observable<PadContact> {
    return this.http.get<PadContact>(`/api/v1/courier/pad/${tenant}`);
  }

  openConfirmSendToBankModal() {
    return this.modalService.addModal<boolean>(ApproveModalComponent, {
      title: 'captureTransaction.sendToBankModal.title',
      description: 'captureTransaction.sendToBankModal.description',
    });
  }

  openDeleteModal() {
    return this.modalService.addModal<boolean>(ApproveModalComponent, {
      title: 'identifyDocument.deleteModal.title',
      description: 'identifyDocument.deleteModal.description',
    });
  }

  get documentTypes() {
    const mappedItems = lodashMap(DOCUMENT_TYPES_BUTTONS, (item: any) => {
      return {
        label: this.translateService.instant(`documentType.${item.type}`),
        value: item.type,
        isOldBank: item.isOldBank,
      };
    });

    return [
      filter(mappedItems, { isOldBank: true }),
      filter(mappedItems, { isOldBank: false }),
    ];
  }
}
